import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  ShieldCheckIcon,
  UserIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  DocumentTextIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Risk level badge
const RiskLevelBadge = ({ level, size = 'md' }) => {
  const colors = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800'
  };
  
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base'
  };
  
  return (
    <span className={`font-semibold rounded-full ${colors[level] || colors.low} ${sizes[size]}`}>
      {level?.charAt(0).toUpperCase() + level?.slice(1) || 'Unknown'}
    </span>
  );
};

// Large risk score display
const RiskScoreDisplay = ({ score }) => {
  const getColor = (s) => {
    if (s >= 80) return 'text-red-600';
    if (s >= 60) return 'text-orange-600';
    if (s >= 30) return 'text-yellow-600';
    return 'text-green-600';
  };
  
  return (
    <div className="text-center">
      <div className={`text-5xl font-bold ${getColor(score)}`}>{score}</div>
      <div className="text-sm text-gray-500 mt-1">Risk Score</div>
    </div>
  );
};

// Action button component
const ActionButton = ({ onClick, loading, icon: Icon, label, variant = 'default', disabled }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    danger: 'bg-red-100 text-red-700 hover:bg-red-200',
    warning: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
    success: 'bg-green-100 text-green-700 hover:bg-green-200',
    primary: 'bg-primary-100 text-primary-700 hover:bg-primary-200'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${variants[variant]}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
};

const RiskUserDetail = () => {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [notesChanged, setNotesChanged] = useState(false);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/risk/users/${userId}`);
      setData(response.data.data);
      setNotes(response.data.data.profile?.admin_notes || '');
    } catch (error) {
      console.error('Error fetching user risk data:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleAction = async (action, requireReason = false, additionalBody = {}) => {
    let reason = null;
    if (requireReason) {
      reason = window.prompt(`Enter reason for ${action}:`);
      if (!reason) {
        toast.error('Reason is required');
        return;
      }
    }
    
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/api/admin/risk/users/${userId}/${action}`, { reason, ...additionalBody });
      toast.success(`Action "${action}" completed successfully`);
      fetchUserData();
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      toast.error(`Failed to perform ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/api/admin/risk/users/${userId}/notes`, { notes });
      toast.success('Notes saved');
      setNotesChanged(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setActionLoading(false);
    }
  };

  const profile = data?.profile || {};
  const signals = data?.signals || [];
  const actions = data?.actions || [];
  const similarUsers = data?.similarUsers || [];
  const websites = data?.websites || [];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/risk/users"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {profile.name || profile.email || 'Unknown User'}
              </h1>
              <p className="text-gray-500">{profile.email}</p>
            </div>
          </div>
          <button
            onClick={fetchUserData}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Risk Overview Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Risk Overview</h2>
              <div className="grid grid-cols-3 gap-6">
                <RiskScoreDisplay score={profile.risk_score || 0} />
                <div className="text-center">
                  <RiskLevelBadge level={profile.risk_level} size="lg" />
                  <div className="text-sm text-gray-500 mt-2">Risk Level</div>
                </div>
                <div className="text-center">
                  <div className="flex justify-center gap-2 flex-wrap">
                    {profile.is_flagged && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-sm">Flagged</span>
                    )}
                    {profile.is_blocked && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm">Blocked</span>
                    )}
                    {profile.requires_captcha && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">Captcha</span>
                    )}
                    {profile.requires_email_otp && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">OTP</span>
                    )}
                    {!profile.is_flagged && !profile.is_blocked && !profile.requires_captcha && !profile.requires_email_otp && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">Clear</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-2">Status</div>
                </div>
              </div>
              
              {/* Risk Factors */}
              {profile.risk_factors && Object.keys(profile.risk_factors).length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Risk Factors</h3>
                  <div className="space-y-2">
                    {Object.entries(profile.risk_factors).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{key.replace(/_/g, ' ')}</span>
                        <span className="text-sm font-medium text-gray-900">+{value.contribution}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
              <div className="flex flex-wrap gap-3">
                {!profile.is_flagged ? (
                  <ActionButton
                    onClick={() => handleAction('flag', true)}
                    loading={actionLoading}
                    icon={ExclamationTriangleIcon}
                    label="Flag User"
                    variant="warning"
                  />
                ) : (
                  <ActionButton
                    onClick={() => handleAction('unflag')}
                    loading={actionLoading}
                    icon={ExclamationTriangleIcon}
                    label="Remove Flag"
                    variant="default"
                  />
                )}
                
                {!profile.is_blocked ? (
                  <ActionButton
                    onClick={() => handleAction('block', true)}
                    loading={actionLoading}
                    icon={NoSymbolIcon}
                    label="Block User"
                    variant="danger"
                  />
                ) : (
                  <ActionButton
                    onClick={() => handleAction('unblock')}
                    loading={actionLoading}
                    icon={NoSymbolIcon}
                    label="Unblock User"
                    variant="success"
                  />
                )}
                
                <ActionButton
                  onClick={() => handleAction('require-captcha', false, { require: !profile.requires_captcha })}
                  loading={actionLoading}
                  icon={ShieldCheckIcon}
                  label={profile.requires_captcha ? 'Remove Captcha' : 'Require Captcha'}
                  variant={profile.requires_captcha ? 'default' : 'primary'}
                />
                
                <ActionButton
                  onClick={() => handleAction('require-otp', false, { require: !profile.requires_email_otp })}
                  loading={actionLoading}
                  icon={ShieldCheckIcon}
                  label={profile.requires_email_otp ? 'Remove OTP' : 'Require OTP (Silent)'}
                  variant={profile.requires_email_otp ? 'default' : 'primary'}
                />
              </div>
              
              {profile.block_reason && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-700">
                    <strong>Block Reason:</strong> {profile.block_reason}
                  </p>
                  {profile.blocked_at && (
                    <p className="text-xs text-red-500 mt-1">
                      Blocked at: {new Date(profile.blocked_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
              
              {profile.flag_reason && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    <strong>Flag Reason:</strong> {profile.flag_reason}
                  </p>
                </div>
              )}
            </div>

            {/* Signals Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Signal History</h2>
              {signals.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {signals.map((signal) => (
                    <div key={signal.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        signal.risk_contribution >= 20 ? 'bg-red-500' :
                        signal.risk_contribution >= 10 ? 'bg-orange-500' :
                        'bg-yellow-500'
                      }`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {signal.signal_type.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(signal.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{signal.signal_value}</p>
                        {signal.risk_contribution > 0 && (
                          <span className="text-xs text-red-600">+{signal.risk_contribution} risk</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No signals recorded</p>
              )}
            </div>

            {/* Action History */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Action History</h2>
              {actions.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {actions.map((action) => (
                    <div key={action.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <ClockIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {action.action_type.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(action.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{action.action_reason}</p>
                        {action.performed_by_email && (
                          <p className="text-xs text-gray-500 mt-1">By: {action.performed_by_email}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No actions recorded</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* User Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">User Info</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <UserIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Account Tier</p>
                    <p className="text-sm font-medium">{profile.account_tier || 'trial'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Last IP</p>
                    <p className="text-sm font-medium">{profile.last_seen_ip || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <DevicePhoneMobileIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Fingerprint</p>
                    <p className="text-sm font-medium truncate max-w-40" title={profile.current_fingerprint}>
                      {profile.current_fingerprint?.slice(0, 16) || '-'}...
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <ClockIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Registered</p>
                    <p className="text-sm font-medium">
                      {profile.user_created_at ? new Date(profile.user_created_at).toLocaleDateString() : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Similar Users */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <LinkIcon className="w-5 h-5" />
                Similar Users
              </h2>
              {similarUsers.length > 0 ? (
                <div className="space-y-2">
                  {similarUsers.map((user) => (
                    <Link
                      key={user.user_id}
                      to={`/risk/users/${user.user_id}`}
                      className="block p-2 bg-gray-50 rounded hover:bg-gray-100"
                    >
                      <p className="text-sm font-medium text-gray-900">{user.email}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">{user.match_type}</span>
                        <span className="text-xs font-medium">Score: {user.risk_score}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No similar users found</p>
              )}
            </div>

            {/* Websites */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Websites ({websites.length})</h2>
              {websites.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {websites.map((site) => (
                    <Link
                      key={site.id}
                      to={`/websites/${site.id}`}
                      className="block p-2 bg-gray-50 rounded hover:bg-gray-100"
                    >
                      <p className="text-sm font-medium text-gray-900">{site.name || 'Unnamed'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          site.status === 'completed' ? 'bg-green-100 text-green-700' :
                          site.status === 'generating' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {site.status}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(site.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No websites</p>
              )}
            </div>

            {/* Admin Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DocumentTextIcon className="w-5 h-5" />
                Admin Notes
              </h2>
              <textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setNotesChanged(true); }}
                placeholder="Add notes about this user..."
                className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {notesChanged && (
                <button
                  onClick={handleSaveNotes}
                  disabled={actionLoading}
                  className="mt-2 w-full py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  Save Notes
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default RiskUserDetail;
